import * as React from 'react';
import { type LoggedInAgencyUserFieldsFragment, SubscriptionEntitlement } from '~/api/graphql';

type EntitlementsContextValue = {
  entitlements: SubscriptionEntitlement[];
  hasEntitlement: (entitlement: SubscriptionEntitlement) => boolean;
};

const EntitlementsContext = React.createContext<EntitlementsContextValue | null>(null);

/**
 * Provides the current agency's subscription entitlements to descendants.
 * Mounted inside the same Await as PermissionsProvider so agencyUser is
 * guaranteed resolved by the time descendants call useEntitlements. This
 * collapses the loading state into a single root-level checkpoint and
 * removes per-component inline queries that would otherwise race the
 * deferred route loader.
 */
export const EntitlementsProvider: React.FC<{
  agencyUser: LoggedInAgencyUserFieldsFragment;
  children: React.ReactNode;
}> = ({ agencyUser, children }) => {
  const entitlements = agencyUser.agency?.entitlements ?? [];

  const hasEntitlement = React.useCallback(
    (entitlement: SubscriptionEntitlement) => entitlements.includes(entitlement),
    [entitlements]
  );

  const value = React.useMemo(
    () => ({ entitlements, hasEntitlement }),
    [entitlements, hasEntitlement]
  );

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
};

/**
 * Access entitlements context. Throws if called outside an EntitlementsProvider
 * tree, mirroring the usePermissions contract.
 *
 * @example
 * const { hasEntitlement } = useEntitlements();
 * if (hasEntitlement(SubscriptionEntitlement.VisibilityControl)) {
 *   // Show visibility-aware UI
 * }
 */
export const useEntitlements = () => {
  const context = React.useContext(EntitlementsContext);
  if (!context) {
    throw new Error('useEntitlements must be used within an EntitlementsProvider');
  }
  return context;
};
